// Esiintyjaprofiili.jsx
// -------------------------------------------------------------
// Huom: Tämä tiedosto on siistitty ESLint-virheistä (no-undef, no-unused-vars).
//       isFreeish lasketaan komponentin sisällä ja sitä myös käytetään JSX:ssä.
// -------------------------------------------------------------

import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import GigCalendar from "../Keikkakalenteri/Keikkakalenteri";
import BookingForm from "../Bookkauslomake/Bookkauslomake";
import { MdArrowBack } from "react-icons/md";
import { Helmet } from "@dr.pogodin/react-helmet";
import { api } from "../../services/api";
import { fetchBandBySlug } from "../../services/bands";
import NotFound from "../../Pages/NotFound/NotFound.jsx";
import { PageOverlayLoading } from "../Loading/Spinner.jsx"; // ⬅️ polku samaksi kuin muualla projektissa
import BookingHint from "components/BookingHint/BookingHint";

import {
  FaFacebook,
  FaInstagram,
  FaYoutube,
  FaStar,
  FaRegStar,
} from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6";
import BandGallery from "../Kuvagalleria/Kuvagalleria";
import ResponsiveButton from "../ResponsiveButton/NeonButton.jsx";
// Media-apurit (absolutoi URL:t, placeholder yms.)
import { absolutize, getProfileSrc, BAND_PLACEHOLDER } from "../../utils/media.js";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs.jsx";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";

/* ---------- Asetukset ---------- */
const SERVICE_NAME = process.env.REACT_APP_SERVICE_NAME || "Buukkeri";

const PROMO_UNLOCK_MODE = String(
  (typeof window !== "undefined" ? window.__PROMO_UNLOCK_MODE__ : undefined) ??
  process.env.REACT_APP_PROMO_UNLOCK_MODE ??
  "within30"
).toLowerCase();

const PROMO_OVERLAY_DISABLED = /^(1|true|yes|on)$/i.test(
  String(
    (typeof window !== "undefined" ? window.__PROMO_OVERLAY_DISABLED__ : undefined) ??
    process.env.REACT_APP_PROMO_OVERLAY_DISABLED ??
    ""
  )
);

/* ---------- Promo-asetukset (optimoitu 24 kk) ---------- */
const PROMO_SUMMARY_MONTHS = (() => {
  const w = typeof window !== "undefined" ? window : undefined;
  const raw =
    (w && w.__PROMO_SUMMARY_MONTHS__) ??
    process.env.REACT_APP_PROMO_SUMMARY_MONTHS ??
    24;
  const n = parseInt(String(raw), 10);
  // karsitaan järkevään haarukkaan 1–36 kk
  return Number.isFinite(n) ? Math.min(Math.max(n, 1), 36) : 24;
})();

/* within30-tilan koonti: pieni ikkuna riittää (oletus 2 kk).
   Ylikirjoita halutessa:
   - window.__PROMO_WITHIN30_SUMMARY_MONTHS__  (devissä)
   - REACT_APP_PROMO_WITHIN30_SUMMARY_MONTHS   (build-aikana)
*/
const PROMO_WITHIN30_SUMMARY_MONTHS = (() => {
  const w = typeof window !== "undefined" ? window : undefined;
  const raw =
    (w && w.__PROMO_WITHIN30_SUMMARY_MONTHS__) ??
    process.env.REACT_APP_PROMO_WITHIN30_SUMMARY_MONTHS ??
    2;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) ? Math.min(Math.max(n, 1), 6) : 2;
})();

/* ---------- Yhtenäinen API-base (sis. prefixin) ---------- */
// (siirretty ylemmäs; ei vaikutusta toimintaan)

/* ---------- Yhtenäinen API-base (sis. prefixin) ---------- */
function getApiBase() {
  const w = typeof window !== "undefined" ? window : undefined;
  const override = w && w.__API_BASE__;
  if (override) return String(override).replace(/\/+$/, "");
  const base = String(process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
  const prefixRaw = String(process.env.REACT_APP_API_PREFIX || "");
  const prefix = prefixRaw ? `/${prefixRaw.replace(/^\/+/, "")}` : "";
  const combined = `${base}${prefix}`.replace(/\/+$/, "");
  return combined || "/api";
}

/* ---------- Fade helperit ---------- */
const PageFade = ({ children }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), 40);
    return () => clearTimeout(id);
  }, []);
  return (
    <div
      className={[
        "opacity-0",
        "transition-opacity duration-[900ms] ease-out",
        "will-change-[opacity]",
        show && "opacity-100",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
};

function useScrollFade(selector = "[data-fade]") {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const els = Array.from(document.querySelectorAll(selector));
    if (!els.length) return;

    els.forEach((el) => {
      el.classList.add(
        "opacity-0",
        "transition-opacity",
        "duration-[900ms]",
        "ease-out",
        "will-change-[opacity]"
      );
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            target.classList.remove("opacity-0");
            target.classList.add("opacity-100");
            io.unobserve(target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -10% 0px" }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [selector]);
}

/* ---------- SEO-apurit ---------- */

// Onko palvelimen canonical jo läsnä? (estää duplikaatti-canonicalin)
function useServerSeoPresent() {
  const [present, setPresent] = useState(false);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const hasCanonical = document.querySelector('link[rel="canonical"]');
    if (hasCanonical) setPresent(true);
  }, []);
  return present;
}

// Siisti kuvaus ja katkaise sananrajasta maxLen:iin
function cleanMetaDescription(text, maxLen = 160) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= maxLen) return s;
  const cut = s.slice(0, maxLen + 1);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 120 ? cut.slice(0, lastSpace) : s.slice(0, maxLen)).trim();
}

// (poistettu availability-pohjainen tarkistus; korvataan summary-pohjaisella)

/* ---------- Pääkomponentti ---------- */
const BandProfile = ({ mode = "public" }) => {
  
  useScrollFade();

  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // band on käytössä jo slugOrId-memossa, joten määritä se ennen käyttöä
  const [band, setBand] = useState(null);

  const handleCopyBandUrl = useCallback(() => {
    console.log("[BandProfile] Kopiointinappia klikattiin");

    if (!band?.slug) return;

    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    if (!origin) return;

    // aina puhdas profiili-URL ilman päivämäärä- tai muita parametreja
    const url = `${origin}/esiintyja/${band.slug}/`;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          toast.success("Profiilin osoite kopioitu leikepöydälle.");
        })
        .catch(() => {
          toast.error("Osoitteen kopiointi epäonnistui.");
        });
    } else {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        toast.success("Profiilin osoite kopioitu leikepöydälle.");
      } catch {
        toast.error("Osoitteen kopiointi epäonnistui.");
      }
    }
  }, [band?.slug]);

  const [inactiveProfile, setInactiveProfile] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Esikatselu kytkeytyy vain erillisessä /preview/… -reitissä tai propilla
  // (ei enää URL-paramista, jotta julkisissa linkeissä ei näy esikatselunappi)

 const qs = new URLSearchParams(location.search || "");
 const isPreview =
   mode === "preview" ||
   (typeof location?.pathname === "string" &&
     (
       location.pathname.startsWith("/preview/esiintyja/") ||
       // salli myös julkinen profiilipolku, jos queryssä on preview-lippu tai token
       (location.pathname.startsWith("/esiintyja/") &&
        (qs.has("preview") || qs.has("previewToken") || qs.has("preview_token")))
     )
   );

  // Esikatselu: piilota header/footer ja estä klikkaukset sisällöstä – skrollaus säilyy
  useEffect(() => {
    if (!isPreview) return;

    const style = document.createElement("style");
    style.setAttribute("data-preview-css", "1");
    style.textContent = `
      /* Piilota globaalit kromit esikatselussa */
      header, nav, .navbar, footer, .site-footer, .CookieConsent, .cookie, .breadcrumbs {
        display: none !important;
      }

      /* Estä linkit/lomakkeet vain esikatselusisällössä */
      [data-preview="true"] a,
      [data-preview="true"] button,
      [data-preview="true"] input,
      [data-preview="true"] select,
      [data-preview="true"] textarea,
      [data-preview="true"] [role="button"] {
        pointer-events: none !important;
        filter: grayscale(0.1);
        opacity: .92;
        cursor: default !important;
      }
      /* Jos haluat sallia jonkin elementin: lisää sille className="preview-allow" */
      [data-preview="true"] .preview-allow { pointer-events: auto !important; filter: none; opacity: 1; }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, [isPreview]);

  // Tuliko siirtymä dashboardista? Vain history state kelpaa
  // (ei URL-parami, jottei nappi näy julkisille linkeille).
  const fromDashboard = Boolean(location?.state?.fromDashboard);


  // ✅ Kutsu hookeja ennen varhaisia paluita (eslint)
  const serverSeoPresent = useServerSeoPresent();

  useEffect(() => {
  if (!band) return;

  console.debug("Band debug:", {
    instrument_labels: band.instrument_labels,
    additional_service_labels: band.additional_service_labels,
    instruments_string: band.instruments_string,
    additional_services_string: band.additional_services_string,
  });

  // helppo tapa kurkata selaimen konsolista:
  window.__band = band;
}, [band]);

  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(Date.now());

  // Päivitä näkymä kun ikkuna saa fokuksen tai välilehti tulee näkyviin
  useEffect(() => {
    const bump = () => setRefreshKey(Date.now());
    const onVisible = () => { if (document.visibilityState === "visible") bump(); };
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // (Valinnainen) dashboardista voi lähettää pingin:
  // localStorage.setItem('bilebox:calendar-touched', String(Date.now()))
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bilebox:calendar-touched") setRefreshKey(Date.now());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ⬇️ LISÄÄ TÄHÄN — sama välilehti, custom-event
useEffect(() => {
  const bump = () => setRefreshKey(Date.now());
  window.addEventListener('bb:calendar:touched', bump);
  return () => window.removeEventListener('bb:calendar:touched', bump);
}, []);

  const [selectedDate, setSelectedDate] = useState(null); // <-- käytetään alla
  const [quoteSent, setQuoteSent] = useState(false);      // tarjouspyyntö lähetetty?
  const [timeLeft, setTimeLeft] = useState(null);

  // Kelluva "Varaa heti" -nappi: näkyvyys skrollisuunnan mukaan
  const [showFloatingCta, setShowFloatingCta] = useState(true);
  const lastScrollYRef = useRef(0);

  // --- Apuri: onko YYYY-MM-DD ---
const isYmd = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);

// --- URL-avaimet palautteelle ---
const urlEmail = (qs.get("email") || "").trim();
const urlDate  = (qs.get("date")  || "").trim();
const urlPin   = (qs.get("pin")   || "").replace(/\D/g, "");
const urlFb    = String(qs.get("feedback") || "");
const wantsFeedbackFlag = urlFb === "1" || urlFb.toLowerCase() === "true";
const hasKeys = !!(urlEmail && isYmd(urlDate) && /^\d{6}$/.test(urlPin));
const forceFeedback = wantsFeedbackFlag || hasKeys;


  // --- Lomakkeen moodi ylös (message | quote | feedback) ---
  const [formMode, setFormMode] = useState(forceFeedback ? "feedback" : null);
  const isFeedbackActive = forceFeedback || formMode === "feedback";

  // --- Kalenterin tyhjyyden toteaminen (ennnen varhaisia paluita) ---
  const serverSaysEmpty =
    band?.meta?.calendar_summary?.is_empty === true ||
    band?.meta?.calendar_empty === true;
  const [calEmptyClient, setCalEmptyClient] = useState(null); // null = ei tiedossa
  const [summaryReady, setSummaryReady] = useState(false);    // summary-haku valmis?

  const calRootRef = useRef(null);   // kalenterikortin root
  const formRootRef = useRef(null);  // lomakekortin root

  const seenNonEmptyRef = useRef(false);
  // Kalenteri on "ready" vasta kun se on antanut ensimmäisen datasignaalin
  const [calendarReady, setCalendarReady] = useState(false);
  // Onko 30 pv sisällä vähintään yksi pe+la -pari auki?
  const [hasFriSatPair, setHasFriSatPair] = useState(null); // null = ei vielä tiedossa
  // Onko YLIPÄÄTÄÄN tulevaisuudessa vähintään yksi pe+la -pari auki? (summarystä)
  const [hasAnyFutureFriSatPair, setHasAnyFutureFriSatPair] = useState(null);

  const handleHasMarkedChange = useCallback(() => {
  // Tulkitaan vain että kalenteri on renderöitynyt → valmis
  setCalendarReady(true);
}, []);

// Kelluvan "Varaa heti" -napin scroll-handler:
// - desktop: skrollaa lomakkeelle
// - mobiili: skrollaa kalenterin kohdalle
const handleScrollToBooking = useCallback(() => {
  if (typeof window === "undefined") return;

  const isDesktop = window.matchMedia
    ? window.matchMedia("(min-width: 768px)").matches
    : window.innerWidth >= 768;

  if (isDesktop) {
    // Desktop → lomake
    const target =
      formRootRef.current || document.getElementById("contact-form");
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    return;
  }

  // Mobiili → kalenteri
  const target = calRootRef.current;
  if (target?.scrollIntoView) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}, []);

  // Piilota kelluva "Varaa heti" -nappi, kun skrollataan alas;
  // näytä kun skrollataan ylöspäin tai ollaan lähellä yläreunaa.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleScrollVisibility = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      const lastY = lastScrollYRef.current;
      const delta = y - lastY;
      lastScrollYRef.current = y;

      const threshold = 200; // px: ennen tätä nappi näkyy aina

      if (y < threshold) {
        setShowFloatingCta(true);
        return;
      }

      // Pieni tärinä/jitteri ignoorataan
      if (Math.abs(delta) < 5) return;

      if (delta > 0) {
        // skrollataan alas → piilota
        setShowFloatingCta(false);
      } else {
        // skrollataan ylös → näytä
        setShowFloatingCta(true);
      }
    };

    window.addEventListener("scroll", handleScrollVisibility, {
      passive: true,
    });
    return () => window.removeEventListener("scroll", handleScrollVisibility);
  }, [lastScrollYRef]);

 useEffect(() => {
  setHasFriSatPair(null);
}, [band?.id, refreshKey]);


  const VALID_STATUSES = useMemo(
    () => new Set(["open","booked","pending","closed","blackout"]),
    []
  );

const inferEmptyFromPayload = React.useCallback((j) => {
  const s = j?.summary ?? j?.data ?? j;
  if (typeof s?.is_empty === "boolean") return !!s.is_empty;
  const counts = s?.counts ?? s?.by_status;
  if (counts && typeof counts === "object") {
    const sum = Object.values(counts).reduce((a,b)=>a+Number(b||0),0);
    return sum === 0;
  }
  const days1 = Array.isArray(s?.days) ? s.days : [];
  const days2 = Array.isArray(s?.months)
    ? s.months.flatMap(m => (m.weeks||[]).flatMap(w => w.days||[]))
    : [];
  const days = days1.length ? days1 : days2;
  if (days.length) {
    const marked = days.filter(d =>
      VALID_STATUSES.has(String(d?.status||"").toLowerCase())
    ).length;
    return marked === 0;
  }
  return null;
}, [VALID_STATUSES]);

// Poimi summary-payloadista lista merkittyjä päiviä ja tarkista pe–la -pari
const computeFriSatPairFromSummary = React.useCallback((j) => {
  const s = j?.summary ?? j?.data ?? j;
  const collectDays = () => {
    const out = [];
    const push = (d) => {
      const iso = String(d?.iso || d?.date || d?.ymd || "").slice(0, 10);
      const st  = String(d?.status || "").toLowerCase();
      if (iso && st) out.push({ iso, status: st });
    };
    if (Array.isArray(s?.days)) s.days.forEach(push);
    if (Array.isArray(s?.months)) {
      s.months.forEach(m => (m.weeks || []).forEach(w => (w.days || []).forEach(push)));
    }
    return out;
  };
  const days = collectDays();
  if (!days.length) return null;
  const open = new Set(days.filter(d => d.status === "open").map(d => d.iso));
  const pad = (n) => String(n).padStart(2, "0");
  const plus1 = (ymd) => {
    const [y, m, d] = ymd.split("-").map(Number);
    const dt = new Date(y, m - 1, d, 12, 0, 0); // paikallinen keskipäivä
    dt.setDate(dt.getDate() + 1);
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  };
  const isFri = (ymd) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0).getDay() === 5;
  };
  for (const iso of open) {
    if (isFri(iso) && open.has(plus1(iso))) return true;
  }
  return false;
}, []);

// Sama kuin yllä, mutta rajaa tarkastelun [fromYmd, toYmd] -ikkunaan
const computeFriSatPairInWindowFromSummary = React.useCallback((j, fromYmd, toYmd) => {
  const s = j?.summary ?? j?.data ?? j;
  const collect = () => {
    const out = [];
    const push = (d) => {
      const iso = String(d?.iso || d?.date || d?.ymd || "").slice(0, 10);
      const st  = String(d?.status || "").toLowerCase();
      if (iso && st) out.push({ iso, status: st });
    };
    if (Array.isArray(s?.days)) s.days.forEach(push);
    if (Array.isArray(s?.months)) {
      s.months.forEach(m => (m.weeks || []).forEach(w => (w.days || []).forEach(push)));
    }
    return out;
  };
  const between = (ymd) => (!fromYmd || ymd >= fromYmd) && (!toYmd || ymd <= toYmd);
  const days = collect().filter(d => between(d.iso));
  if (!days.length) return null;
  const open = new Set(days.filter(d => d.status === "open").map(d => d.iso));
  const pad = (n) => String(n).padStart(2, "0");
  const plus1 = (ymd) => {
    const [y, m, d] = ymd.split("-").map(Number);
    const dt = new Date(y, m - 1, d, 12, 0, 0);
    dt.setDate(dt.getDate() + 1);
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  };
  const isFri = (ymd) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0).getDay() === 5;
  };
  for (const iso of open) if (isFri(iso) && open.has(plus1(iso))) return true;
  return false;
}, []);

// Älä nosta verhoa pelkän palvelinvihjeen perusteella – pidä tri-state null,
// kunnes summary- tai DOM-havainto vahvistaa.
useEffect(() => {
  if (!serverSaysEmpty) return;
  if (seenNonEmptyRef.current) return;
  setCalEmptyClient((v) => v ?? null);
}, [serverSaysEmpty]);


useEffect(() => {
  if (!band?.id) return;
  let aborted = false;
  setSummaryReady(false);

  (async () => {
      try {
        const base = getApiBase();
      const id = encodeURIComponent(band.id);
      const today = new Date();
      const from = today.toISOString().slice(0, 10);
      const qs1 = new URLSearchParams({ from, months: String(PROMO_SUMMARY_MONTHS) }).toString();
      const candidates = [
        `${base}/bands/${id}/calendar/summary?${qs1}`,
        `${base}/calendar/summary?target_type=band&target_id=${id}&${qs1}`,
      ];

      for (const url of candidates) {
        try {
          const r = await fetch(url, {
            credentials: "include",
            headers: { Accept: "application/json" },
          });
          if (!r.ok) continue;

          const j = await r.json();
          const inferredEmpty = inferEmptyFromPayload(j);
          // Laske myös “anyFuture” pe–la -pari summaryn perusteella
          try {
            const anyPair = computeFriSatPairFromSummary(j);
            setHasAnyFutureFriSatPair(anyPair);
          } catch {}

          if (aborted) return;

          // jos aiemmin havaittu ei-tyhjä, älä heiluta enää tilaa
          if (seenNonEmptyRef.current) {
            setSummaryReady(true);
            return;
          }

          if (inferredEmpty === false) {
            seenNonEmptyRef.current = true;
            setCalEmptyClient(false);
          } else if (inferredEmpty === true) {
            setCalEmptyClient(true);
          } else {
            setCalEmptyClient(null);
          }

          setSummaryReady(true);
          return;
        } catch {}
      }

      if (!aborted) {
        setCalEmptyClient(null);
        setSummaryReady(true);
      }
    } catch {
      if (!aborted) {
        setCalEmptyClient(null);
        setSummaryReady(true);
      }
    }
  })();

  return () => { aborted = true; };
}, [band?.id, refreshKey, inferEmptyFromPayload, computeFriSatPairFromSummary]);


// Kun bändi vaihtuu, nollaa havaintoliput ja anna logiikan päättää tila uudelleen
useEffect(() => {
  seenNonEmptyRef.current = false;
  setCalEmptyClient(null);
}, [band?.id]);

// within30: käytä summary-koontia (kevyt) 30 pv ikkunaan rajattuna
useEffect(() => {
  if (PROMO_UNLOCK_MODE !== "within30") return;
  if (!band?.id) return;

  let aborted = false;
  (async () => {
    try {
      const base = getApiBase();
      const id   = encodeURIComponent(band.id);
      const today = new Date();
      const fromYmd = today.toISOString().slice(0, 10);
      const toDate = new Date(today); toDate.setDate(toDate.getDate() + 30);
      const toYmd = toDate.toISOString().slice(0, 10);

      const qs1 = new URLSearchParams({ from: fromYmd, months: String(PROMO_WITHIN30_SUMMARY_MONTHS) }).toString();
      const candidates = [
        `${base}/bands/${id}/calendar/summary?${qs1}`,
        `${base}/calendar/summary?target_type=band&target_id=${id}&${qs1}`,
      ];

      let decided = false;
      for (const url of candidates) {
        try {
          const r = await fetch(url, { credentials: "include", headers: { Accept: "application/json" } });
          if (!r.ok) continue;
          const j = await r.json();
          const anyPair = computeFriSatPairInWindowFromSummary(j, fromYmd, toYmd);
          if (aborted) return;

          setCalendarReady(true);
          if (anyPair === true) {
            seenNonEmptyRef.current = true;
            setHasFriSatPair(true);
          } else if (anyPair === false) {
            setHasFriSatPair(false);
          } else {
            setHasFriSatPair(null);
          }
          decided = true;
          break;
        } catch {}
      }
      if (!decided && !aborted) { setCalendarReady(true); setHasFriSatPair(null); }
    } catch { if (!aborted) { setCalendarReady(true); setHasFriSatPair(null); } }
  })();

  return () => { aborted = true; };
}, [band?.id, refreshKey, computeFriSatPairInWindowFromSummary]);

// Verho-sääntö:
//  - within30: kalenteri valmis JA 30 pv sisällä ei ole pe–la -paria
//  - anyFuture: summary haettu JA missään tulevassa ei ole pe–la -paria
 const showInactiveOverlay =
  !isPreview && !PROMO_OVERLAY_DISABLED && !seenNonEmptyRef.current && (
    (PROMO_UNLOCK_MODE === "within30" && calendarReady && hasFriSatPair === false) ||
    (PROMO_UNLOCK_MODE === "anyfuture" && summaryReady && hasAnyFutureFriSatPair === false)
  );

useEffect(() => {
  console.debug("promolock", {
    mode: PROMO_UNLOCK_MODE,
    overlayDisabled: PROMO_OVERLAY_DISABLED,
    hasFriSatPair,
    hasAnyFutureFriSatPair,
    serverSaysEmpty,
    calEmptyClient,
    calendarReady,
    summaryReady,
    isPreview,
    showInactiveOverlay
  });
}, [hasFriSatPair, hasAnyFutureFriSatPair, serverSaysEmpty, calEmptyClient, calendarReady, summaryReady, isPreview, showInactiveOverlay]);

// Yhteinen verhoelementti (voidaan käyttää missä vain return-haarassa)
const inactiveOverlayEl = showInactiveOverlay ? (
  <div className="fixed inset-0 z-[9999] grid place-items-center pointer-events-none p-4">
    <div className="absolute inset-0 bg-black/90 backdrop-blur-lg md:backdrop-blur-[8px]" aria-hidden="true" />
    <div className="pointer-events-auto relative w-full max-w-sm sm:max-w-md drop-shadow-xl">
      <div className="rounded-2xl bg-black/85 text-white ring-1 ring-white/10 shadow-xl p-5 sm:p-6 text-center">
        <h2 className="font-customFont2 text-2xl sm:text-xl font-semibold mb-2">
          Huomio!
        </h2>
        <p className="font-customFont1 text-sm sm:text-base opacity-90">
          Buukkeri on tarkoitettu keikkojen buukkaamiseen – ei promoesittelyyn ilman buukattavia päiviä.
          Tämä profiili ei ole juuri nyt näkyvissä, koska {
            PROMO_UNLOCK_MODE === "anyfuture"
              ? "kalenterissa ei ole yhtään tulevaa peräkkäistä pe–la -päiväparia auki."
              : "seuraavan 30 päivän sisällä kalenterissa ei ole yhtään peräkkäistä pe–la -päiväparia auki."
          }
        </p>
      </div>
    </div>
  </div>
) : null;

// === Promo-lukko: piilota kromit ja estä klikkaukset, skrolli sallittu ===
useEffect(() => {
  let styleEl = null;
  if (showInactiveOverlay && !isPreview) {
    styleEl = document.createElement("style");
    styleEl.setAttribute("data-promolock-css", "1");
    styleEl.textContent = `
      /* Piilota globaalit kromit */
      header, nav, .navbar, footer, .site-footer, .CookieConsent, .cookie, .breadcrumbs {
        display: none !important;
      }
      /* Estä kaikki interaktiot sivun sisällössä, mutta anna skrollata */
      [data-promolock="true"] a,
      [data-promolock="true"] button,
      [data-promolock="true"] input,
      [data-promolock="true"] select,
      [data-promolock="true"] textarea,
      [data-promolock="true"] [role="button"],
      [data-promolock="true"] details,
      [data-promolock="true"] summary {
        pointer-events: none !important;
        filter: grayscale(0.06);
        opacity: .92;
        cursor: default !important;
      }
      [data-promolock="true"] .preview-allow { pointer-events:auto !important; filter:none; opacity:1; }
    `;
    document.head.appendChild(styleEl);
  }
  return () => { if (styleEl) styleEl.remove(); };
}, [showInactiveOverlay, isPreview]);

  // Poista julkisesta URL:sta mahdolliset esikatseluun liittyvät parametrit,
  // jotta preview-bar ei enää näy kun ollaan public-näkymässä.
  useEffect(() => {
    if (isPreview) return;
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const before = url.search;
    ["preview", "previewToken", "preview_token", "from"].forEach((k) =>
      url.searchParams.delete(k)
    );
    if (url.search !== before) {
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
    try { window.dispatchEvent(new CustomEvent("bb:preview:disable")); } catch {}
  }, [isPreview]);


  // 1) Lataa kaikki genret (id, name) kerran – tarvitaan ID→nimi -kartoitukseen
const [allGenres, setAllGenres] = useState([]);
useEffect(() => {
  const ac = new AbortController();
  (async () => {
    try {
      const list = await api.genres({ signal: ac.signal }); // /meta/genres → /genres fallback
      setAllGenres(Array.isArray(list) ? list : []);
    } catch {}
  })();
  return () => ac.abort();
}, []);

const genreMap = useMemo(
  () => new Map(allGenres.map(g => [Number(g.id), String(g.name || "").trim()])),
  [allGenres]
);

// 2) Lataa tämän bändin kanoniset genre-ID:t suoraan endpointista
const [bandGenreIds, setBandGenreIds] = useState([]);
useEffect(() => {
  if (!band?.id) return;

  let cancelled = false;
  const base = window.__API_BASE__ || process.env.REACT_APP_API_BASE || "/api";
  const id = encodeURIComponent(band.id);
  const urls = [
    `${base}/bands/${id}/genres`,
    `${base}/admin/bands/${id}/genres`,
  ];

  (async () => {
    let payload = null;
    for (const url of urls) {
      try {
        const r = await fetch(url, {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (r.ok) { payload = await r.json(); break; }
      } catch {}
    }

    // Normalisoi vastaus → [id, id, ...]
    let ids =
      payload?.genre_ids ??
      (Array.isArray(payload?.genres) ? payload.genres.map(g => g.id) : null) ??
      payload?.data?.genre_ids ??
      (Array.isArray(payload?.data) ? payload.data.map(g => g.id) : null) ??
      null;

    // Fallback: jos endpoint ei palauttanut mitään, käytä band-olion ID-listoja jos on
    if (!ids) {
      if (Array.isArray(band?.genre_ids)) ids = band.genre_ids;
      else if (Array.isArray(band?.genres) && typeof band.genres[0] === "number") ids = band.genres;
    }

    if (!cancelled && Array.isArray(ids)) {
      setBandGenreIds(ids.map(n => Number(n)).filter(n => Number.isFinite(n)));
    }
  })();

  return () => { cancelled = true; };
}, [band?.id, band?.genre_ids, band?.genres]);


  const todayYmdHEL = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Helsinki",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date())

  const ratingNumRaw = Number(band?.average_rating ?? band?.rating?.average);
  const ratingAvg = Number.isFinite(ratingNumRaw)
    ? Math.min(5, Math.max(0, ratingNumRaw))
    : 0;

  const countRaw = Number(band?.total_reviews ?? band?.reviews_count ?? band?.rating?.total);
  const ratingCount = Number.isFinite(countRaw) ? Math.max(0, Math.floor(countRaw)) : 0;

  const safeParseArray = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === "string") {
      const trimmed = input.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : [trimmed];
        } catch {}
      }
      return trimmed
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

// "open" ja legacy "free" tulkitaan molemmat buukattaviksi
const isOpen =
  !!selectedDate && /^(open|free)$/i.test(String(selectedDate.status || ""));

 const isPastSelected = React.useMemo(() => {
   const ymd = (selectedDate?.iso || "").slice(0, 10);
   return ymd ? ymd < todayYmdHEL() : false;
 }, [selectedDate?.iso]);

  // Jos URLissa on date → valitse päivä (riittää "open" jotta PIN-check toimii)
  useEffect(() => {
    if (!forceFeedback || !isYmd(urlDate)) return;
    // älä resetoi jos käyttäjä on jo valinnut jotain
    if (selectedDate?.iso === urlDate) return;
    handleDateSelect(urlDate, "open");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceFeedback, urlDate]);

  // Pakkoskrolli lomakkeelle kun tullaan palautelinkillä
  useEffect(() => {
    if (!forceFeedback) return;
    const t = setTimeout(() => {
      document.getElementById("contact-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [forceFeedback]);

// Yleisparseri: tukee YouTube (watch/shorts/live/youtu.be/embed/playlist) ja Vimeo
// Palauttaa objektin { embed, watch } tai null
const toEmbedUrl = (u) => {
  if (!u) return null;
  const s = String(u).trim();

  // --- YouTube playlist (ilman v=) → videoseries/embed ---
  if (/(?:youtu\.be|youtube\.com)/i.test(s)) {
    const list = s.match(/[?&]list=([A-Za-z0-9_-]+)/i);
    const hasV = /[?&]v=/.test(s);
    if (list && !hasV) {
      return {
        embed: `https://www.youtube.com/embed/videoseries?list=${list[1]}`,
        watch: s,
      };
    }
    // Video-ID useista muodoista (11 merkkiä)
    const idMatch = s.match(
      /(?:youtube\.com\/watch\?v=|(?:www\.|m\.)?youtube\.com\/shorts\/|youtu\.be\/|(?:www\.|m\.)?youtube\.com\/live\/|(?:www\.|m\.)?youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/
    );
    if (idMatch) {
      const id = idMatch[1];
      // Alkuajan t=123(s) → start=
      const t = (s.match(/[?&]t=(\d+)(?:s)?/i) || [])[1];
      const start = t ? `&start=${parseInt(t, 10) || 0}` : "";
      const origin =
        typeof window !== "undefined" && window.location?.origin
          ? `&origin=${encodeURIComponent(window.location.origin)}`
          : "";
      const params = `?rel=0&modestbranding=1&playsinline=1${start}${origin}`;
      return {
        // Privacy-enhanced mode vähentää evästeitä → auttaa myös Consent Mode -tilanteissa
        embed: `https://www.youtube-nocookie.com/embed/${id}${params}`,
        watch: `https://www.youtube.com/watch?v=${id}`,
      };
    }
  }

  // --- Vimeo ---
  const vimeo = s.match(/vimeo\.com\/(\d+)/i);
  if (vimeo) {
    return {
      embed: `https://player.vimeo.com/video/${vimeo[1]}`,
      watch: s,
    };
  }

  return null;
};

// --- Videolistat valmiiksi (säilyttää layoutin ja otsikon) ---
const videoItems = useMemo(() => {
  const src = (band?.video_link || "").trim();
  if (!src) return [];
  return src
    .split(/\r?\n/)
    .map((u) => u.trim())
    .filter(Boolean)
    .map(toEmbedUrl)
    .filter(Boolean);
}, [band?.video_link]);


  // Kovennettu normalisointi: vain http(s), ei data:, javascript: jne.
  const normalize = (u) => {
    if (!u) return null;
    const s = String(u).trim();
    if (!s) return null;
    const withProto = /^https?:\/\//i.test(s) ? s : `https://${s}`;
    try {
      const url = new URL(withProto);
      if (!/^https?$/.test(url.protocol.replace(':',''))) return null;
      return url.toString();
    } catch {
      return null;
    }
  };

  // Päivä URL:sta tai localStoragesta (URL voittaa)
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get("date");
  const fromStorage =
    typeof window !== "undefined"
      ? localStorage.getItem("bilebox:selectedDate")
      : null;

// Luo nimi-lista priorisoiden aina endpointista haetut ID:t
const genreNames = useMemo(() => {
  // 1) jos saimme kanoniset id:t, mapita niistä nimet
  if (bandGenreIds?.length) {
    return bandGenreIds.map(id => genreMap.get(Number(id))).filter(Boolean);
  }

  // 2) vaihtoehtoiset muodot bändin payloadissa (fallbackit)
  if (Array.isArray(band?.genre_names)) {
    return band.genre_names.map(s => String(s).trim()).filter(Boolean);
  }
  if (Array.isArray(band?.genres)) {
    if (band.genres.length && typeof band.genres[0] === "object") {
      return band.genres.map(g => String(g?.name || "").trim()).filter(Boolean);
    }
    if (band.genres.length && typeof band.genres[0] === "number") {
      return band.genres.map(id => genreMap.get(Number(id))).filter(Boolean);
    }
  }
  if (Array.isArray(band?.genre_ids)) {
    return band.genre_ids.map(id => genreMap.get(Number(id))).filter(Boolean);
  }
  if (typeof band?.genre === "string") {
    return band.genre.split(",").map(s => s.trim()).filter(Boolean);
  }
  if (Array.isArray(band?.genre)) {
    return band.genre.map(s => String(s).trim()).filter(Boolean);
  }
  return [];
}, [bandGenreIds, band, genreMap]);

const genreText = useMemo(() => {
  // Poista duplikaatit ja rajoita max 6
  const uniq = Array.from(new Set(genreNames.filter(Boolean)));
  return uniq.slice(0, 6).join(", ");
}, [genreNames]);

// --- kaikki maakunnat + id->nimi-kartta
const [allRegions, setAllRegions] = useState([]);
useEffect(() => {
  const ac = new AbortController();
  (async () => {
    try {
      const list = await api.pricing.regions({ signal: ac.signal });
      setAllRegions(Array.isArray(list) ? list : []);
    } catch {}
  })();
  return () => ac.abort();
}, []);

  const regionMap = useMemo(
    () => new Map(
      allRegions.map(r => [
        Number(r.id),
        String(r.name ?? r.region ?? r.label ?? "").trim()
      ])
    ),
    [allRegions]
  );

  // Positiivinen numero tulkitaan oikeaksi hinnaksi
  const hasPositive = useCallback((v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  }, []);

  // --- Kaikkien oikeiden maakuntien ID:t (poista pseudo-ID:t kuten -1)
  const allRegionIds = useMemo(
    () => allRegions
      .map(r => Number(r.id))
      .filter(id => Number.isFinite(id) && id > 0),
    [allRegions]
  );

  // Kattaa maakunnan vain jos siellä on vähintään YHDEN setin hinta (>0).
  // (Ei huomioi pelkkää "base pricea" ilman settiä.)
  const pricedRegionIdSet = useMemo(() => {
    const items = Array.isArray(band?.pricing) ? band.pricing : [];
    const s = new Set();
    for (const p of items) {
      const rid = Number(p?.region_id);
      if (!Number.isFinite(rid) || rid <= 0) continue;

      // 1) Uudempi malli: rivillä on setti-lista
      if (Array.isArray(p?.sets) && p.sets.length) {
        if (p.sets.some(st => hasPositive(st?.price))) {
          s.add(rid);
          continue;
        }
      }
      // 2) Tasainen malli: rivillä on set_label/label + hinta
      const hasSetLabel = typeof p?.set_label === "string" || typeof p?.label === "string";
      if (hasSetLabel && hasPositive(p?.price ?? p?.base_price)) {
        s.add(rid);
        continue;
      }
      // 3) Pelkkää base-hintaa (ilman settiä) EI lasketa mukaan,
      //    koska haluamme “setit ja hinnat” -kriteerin.
    }
    return s;
  }, [band?.pricing, hasPositive]);

  // --- Kattaa kaikki maakunnat? (UI:ssa näytetään "Koko Suomi")
  const coversAllRegions = useMemo(() => {
    if (!allRegionIds.length) return false; // maakuntalista ei vielä ladattu
    for (const id of allRegionIds) {
      if (!pricedRegionIdSet.has(id)) return false;
    }
    return true;
  }, [allRegionIds, pricedRegionIdSet]);


  // pienehkö apuri JSON-tekstille
  const parseMaybeJson = React.useCallback((v) => {
    if (typeof v !== "string") return v;
    const s = v.trim();
    if (!s) return [];
    if (s.startsWith("[") && s.endsWith("]")) {
      try { return JSON.parse(s); } catch {}
    }
    return s;
  }, []);

  // ✅ käyttää regionMap:ia ja on memoitu — voidaan laittaa deps-listoihin
  const toRegionNames = React.useCallback((val) => {
    if (!val) return [];

    if (typeof val === "string") {
      const parsed = parseMaybeJson(val);
      if (Array.isArray(parsed)) return toRegionNames(parsed);
      return val.split(",").map(s => s.trim()).filter(Boolean);
    }

    if (Array.isArray(val)) {
      if (!val.length) return [];
      const first = val[0];
      if (typeof first === "number") {
        return val.map(n => regionMap.get(Number(n))).filter(Boolean);
      }
      if (typeof first === "string") {
        return val.map(s => s.trim()).filter(Boolean);
      }
      if (first && typeof first === "object") {
        return val.map(o =>
          String(o?.name ?? o?.region ?? o?.label ?? o?.region_name ?? "")
            .trim()
        ).filter(Boolean);
      }
    }

    if (typeof val === "object") {
      const s = String(val?.name ?? val?.region ?? val?.label ?? "").trim();
      return s ? [s] : [];
    }
    return [];
  }, [regionMap, parseMaybeJson]);  // ✅ lisää parseMaybeJson tänne

  const regionsText = useMemo(() => {
    // 0) Näytä "Koko Suomi" vain jos hinnasto kattaa kaikki oikeat maakunnat
    if (coversAllRegions) return "Koko Suomi";

    // 0.5) Jos hinnallisia maakuntia on, listaa ne nimillä (ignoreeraa pseudo -1)
    if (pricedRegionIdSet.size > 0) {
      const names = Array.from(pricedRegionIdSet)
        .map(id => regionMap.get(Number(id)))
        .filter(Boolean);
      if (names.length) return Array.from(new Set(names)).join(", ");
    }

    // 1) ensisijaiset lähteet (id:t, objektit, stringit)
    const candidates = [
      band?.performance_region_ids,
      band?.performance_regions,
      band?.performance_regions_names,
      band?.regions,
      band?.regions_names,
      band?.regions_string,
    ];

    for (const c of candidates) {
      const names = toRegionNames(c);
      if (names.length) {
        const uniq = Array.from(new Set(names.map(s => s.trim()).filter(Boolean)));
        return uniq.join(", ");
      }
    }

    // 2) 🔁 fallback hinnoista, jos mikään yllä ei tuottanut arvoja
    const fromPricing = Array.isArray(band?.pricing)
      ? Array.from(
          new Set(
            band.pricing
              .map(p => String(p?.region_name ?? p?.region ?? "").trim())
              .filter(Boolean)
          )
        )
      : [];

    return fromPricing.join(", ");
  }, [
    band?.performance_region_ids,
    band?.performance_regions,
    band?.performance_regions_names,
    band?.regions,
    band?.regions_names,
    band?.regions_string,
    band?.pricing,           
    toRegionNames,
    coversAllRegions,
    pricedRegionIdSet,
    regionMap,
  ]);

const setsText = useMemo(() => {
  const arr = Array.isArray(band?.sets) ? [...band.sets] : [];
  if (!arr.length) return "";

  // Järjestä järkevästi
  arr.sort((a, b) =>
    (a?.position ?? 0) - (b?.position ?? 0) ||
    (a?.set_count ?? 0) - (b?.set_count ?? 0)
  );

  const parts = arr.map(s => {
    const lbl = String(s?.label || "").trim();
    if (lbl) return lbl; // esim. "2x45min"
    const c = Number(s?.set_count);
    const m = Number(s?.minutes_per_set ?? s?.minutes ?? s?.duration);
    if (Number.isFinite(c) && Number.isFinite(m) && c > 0 && m > 0) return `${c}x${m}min`;
    if (Number.isFinite(m) && m > 0) return `${m} min`;
    return "";
  }).filter(Boolean);

  return parts.join(", ");
}, [band?.sets]);

// --- Soittimet & lisäpalvelut tekstiksi ---
// Yleis-apuri: array/object/string -> "a, b, c"
const toComma = (val) => {
  if (!val) return "";
  if (Array.isArray(val)) {
    return val
      .map(v => {
        if (typeof v === "string") return v.trim();
        if (v && typeof v === "object") return String(v.label || v.name || v.title || "").trim();
        return "";
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof val === "string") return val.trim();
  return "";
};

// Soittimet
const instrumentsText =
  toComma(band?.instrument_labels) ||
  (band?.instruments_string?.trim() ?? "") ||
  toComma(band?.instruments);

// Lisäpalvelut
const servicesText =
  toComma(band?.additional_service_labels) ||
  (band?.additional_services_string?.trim() ?? "") ||
  toComma(band?.additional_services);


  let initialDate = isYmd(fromUrl)
    ? fromUrl
    : isYmd(fromStorage)
    ? fromStorage
    : todayYmdHEL();
  if (initialDate < todayYmdHEL()) initialDate = todayYmdHEL();

  useEffect(() => {
    if (!band?.id) return;

    const payload = {
      target_type: "band",
      target_id: Number(band.id),
      slug: band.slug ?? null,
    };

    // ensisijaisesti sendBeacon, jotta osuu myös sivulta poistuttaessa
    if (navigator?.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(`${getApiBase()}/analytics/view`, blob);
      return; // ei tarvitse siivota mitään
    }

 // fallback: sama pyyntö API-basen kautta
 const ctrl = new AbortController();
 const t = setTimeout(() => {
   api.analyticsView(payload, {
     signal: ctrl.signal,
     keepalive: true,       // välittyy req():n kautta fetchille
   }).catch(() => {});
 }, 800);

    return () => { clearTimeout(t); ctrl.abort(); };
  }, [band?.id, band?.slug]);

/* --- 1 h countdown pending-tilalle --- */
useEffect(() => {
  if (selectedDate?.status !== "pending") return;

  const ONE_HOUR = 60 * 60 * 1000;

  const deadline = selectedDate?.expires_at
    ? new Date(selectedDate.expires_at).getTime()
    : selectedDate?.sent_at
    ? new Date(selectedDate.sent_at).getTime() + ONE_HOUR
    : null;

    if (!deadline) return;

    let timer;
    const tick = () => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setTimeLeft("00:00:00");
        setSelectedDate(null); // sulje lomake
        setRefreshKey(Date.now()); // refetch kalenteri
        if (timer) clearInterval(timer);
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0"
        )}:${String(seconds).padStart(2, "0")}`
      );
    };

    tick();
    timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [selectedDate]);

// --- Bändin tiedot ---
useEffect(() => {
  const ac = new AbortController();
  setError("");

  (async () => {
    try {
      // 1) hae slugilla ja huomioi mahdollinen previewToken
      const qs = new URLSearchParams(location.search || "");
      const previewToken = isPreview
        ? (qs.get("previewToken") || qs.get("preview_token") || undefined)
        : undefined;

       const res = await fetchBandBySlug(slug, { previewToken });

      if (!res?.ok) {
        if (res?.code === "inactive_profile") {
          setInactiveProfile(true);
          return;
        }
        if (res?.code === "not_found") {
          // Esikatselussa ei heitetä 404:ää — näytetään mieluummin virheilmoitus
          if (isPreview) { setError("Esikatselua ei löytynyt tälle profiilille."); return; }
          setNotFound(true);
          return;
        }
        setError("Tuntematon virhe esiintyjän latauksessa.");
        return;
      }

      const b = res.band;

      if (!b?.id) {
        setBand(b);
        return;
      }

      // 2) jos listaus pudotti soittimet/palvelut -> hae detail ja mergää
      const missing =
        !b.instrument_labels &&
        !b.instruments_string &&
        !b.additional_service_labels &&
        !b.additional_services_string;

      if (missing) {
        try {
          const base = getApiBase();
          const detailUrl = new URL(`${base}/bands/${encodeURIComponent(b.id)}`, window.location.origin);
          if (previewToken) detailUrl.searchParams.set("previewToken", previewToken);
          const r = await fetch(detailUrl.toString(), {
            headers: {
              Accept: "application/json",
              ...(previewToken ? { "x-preview-token": previewToken } : {}),
            },
            credentials: "include",
            signal: ac.signal,
          });
          if (r.ok) {
            const full = await r.json();
            const fullBand = full?.band || full;  // jos API joskus wräppää { band: {...} }
            setBand({ ...b, ...fullBand });
            return;
          }
        } catch {
          /* nielaise ja käytä b */
        }
      }

      // 3) jos mitään ei puuttunut tai detail epäonnistui
      setBand(b);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Virhe esiintyjän haussa:", err);
        setError("Jotain meni pieleen esiintyjän tietojen hakemisessa.");
      }
    }
  })();

  return () => ac.abort();
}, [slug, location.search, isPreview]); 

  const handleDateSelect = (
    date,
    status = "free",
    sentAt = null,
    expiresAt = null
  ) => {
    setQuoteSent(false); // uusi päivä → näytä banneri taas tarpeen mukaan
    const dateObj = new Date(date);
    const weekday = dateObj
      .toLocaleDateString("fi-FI", { weekday: "long" })
      .replace(/na$/, "");
    const formattedDate = `${weekday} ${dateObj.getDate()}.${
      dateObj.getMonth() + 1
    }.${dateObj.getFullYear()}`;
    const iso = dateObj.toISOString().split("T")[0];

    try { localStorage.setItem("bilebox:selectedDate", iso); } catch {}

    setSelectedDate({
      iso,
      formatted: formattedDate,
      status,
      sent_at: sentAt,
      expires_at: expiresAt,
    });
  };

  if (error) {
    return (
      <>
      <div className="p-5">
        <p className="text-red-500">Virhe: {error}</p>
        <ResponsiveButton onClick={() => navigate(-1)}>Takaisin</ResponsiveButton>
      </div>
      </>
    );
  }

    if (inactiveProfile) {
    return (
      <>

      <div className="p-6 max-w-3xl mx-auto">
        <div className="rounded-xl px-5 py-4 bg-amber-50 text-amber-900 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:ring-amber-700/50">
          <p className="font-semibold">
            Profiili ei ole aktiivinen – esiintyjä ei ota pyyntöjä juuri nyt.
          </p>
          <p className="text-sm opacity-90 mt-1">
            Jos olet omistaja, jäsen tai admin, kirjaudu sisään tai käytä esikatselulinkkiä.
          </p>
        </div>
        <div className="mt-4">
          <ResponsiveButton onClick={() => navigate(-1)}>Takaisin</ResponsiveButton>
        </div>
      </div>
      </>
    );
  }

  if (notFound) {
    // Käytä samaa 404-sivua kuin muualla sivustolla
    return <NotFound />;
  }

     // Lataustila: ei vielä bändiä, mutta ei myöskään virhettä / 404 / inaktiivista profiilia
  if (!band && !error && !inactiveProfile && !notFound) {
    return (
      <>
        {/* Koko sivun pyörivä logo -overlay (sama kuin muualla) */}
        <PageOverlayLoading
          // visuaaliset twiikkaukset – samat arvot kuin muissa näkymissä jos haluat 1:1
          logoGlow={true}
          logoOrbit={true}
          logoPulse={true}
          logoSweep={true}
          markSize={78}     // logon koko
          logoSpinSec={3.2} // kierrosaika sekunneissa
          // bgImage={...}   // halutessa tänne voi myöhemmin laittaa taustakuvan
          // bgLqip={...}    // ja sen pienen LQIP-version
        />

        {/* Pieni varmistus apuvälineille, vaikka overlay ei jostain syystä renderöityisi */}
        <span className="sr-only">Ladataan esiintyjän tietoja…</span>
      </>
    );
  }



  // Kansikuva sivulle (UI): voi olla sama kuin OG, mutta ei pakko
  const coverSrc = getProfileSrc(band) || BAND_PLACEHOLDER;

  // --- OG-kuva prioriteetti ---
  // 1) eksplisiittinen og_image_url
  // 2) profiilikuvan URL (profile_image DB:stä)
  // 3) muu bändikuvan logiikka (getProfileSrc)
  // 4) placeholder vasta viimeisenä
  const pickOgImage = (b) => {
    const cand = [
      absolutize(b?.og_image_url),
      absolutize(b?.profile_image),
      getProfileSrc(b),
      BAND_PLACEHOLDER,
    ].filter(Boolean);
    return cand[0];
  };
  const ogImage = pickOgImage(band);
  const ogWidth  = Number(band?.profile_w || band?.cover_w) || 1200;
  const ogHeight = Number(band?.profile_h || band?.cover_h) || 630;

      // ⬇️ LISÄÄ NÄMÄ ALIASET TÄHÄN
  const socials   = band.socials || {};
  const facebook  = band.facebook  || socials.facebook  || null;
  const instagram = band.instagram || socials.instagram || null;
  const tiktok    = band.tiktok    || socials.tiktok    || null;
  const youtube   = band.youtube   || socials.youtube   || null;

  /* ---------- SEO-datat clienttiin (vain jos SSR-SEO puuttuu) ---------- */

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://spiderlegs.example";
  const canonicalUrl = `${origin}${location.pathname}`;

  // Esikatselussa kerrotaan OG:lle täysi URL (sis. ?preview=…), mutta canonical pidetään puhtaana
  const fullHref =
    (typeof window !== "undefined" && window.location?.href)
      ? window.location.href
      : canonicalUrl;
  const ogUrl = isPreview ? fullHref : canonicalUrl;

  const rawType = (band.artist_type || "Esiintyjä").trim();
  const name = (band.name || "").trim();
  const nameForTitle = name.toLowerCase().startsWith(rawType.toLowerCase())
    ? name
    : `${rawType} ${name}`;
  const typeLabel = rawType; // jos käytät alempana

  const title = `${SERVICE_NAME} | ${nameForTitle} - Varaa keikka`;

  const metaDescription = cleanMetaDescription(
    band.description || `${band.name} – ${typeLabel.toLowerCase()}. Varaa keikka helposti.`
  );

  return (
    <>
      <PageFade>

      <div
        className="pt-12 dark:bg-black dark:bg-opacity-10 pb-10 mt-2"
        data-preview={isPreview ? "true" : "false"}
        data-promolock={showInactiveOverlay ? "true" : "false"}
      >
        {/* Helmet ajetaan aina titlea varten. 
           - Jos palvelin on jo tulostanut canonicalin ja perusmetat (serverSeoPresent=true),
             yliajetaan vain dynaamiset asiat: OG/Twitter -kuva + og:url.
           - Muussa tapauksessa tulostetaan kaikki (title+description+OG+Twitter+canonical).
        */}
        <Helmet>
          {/* Titlea on turvallista päivittää clientissäkin */}

          {/* Esikatselu ei indeksoidu */}
          {isPreview && (
            <meta name="robots" content="noindex,nofollow" />
          )}

          <title>{title}</title>

          {/* Palvelimella EI ole metoja → tulosta koko setti */}
          {!serverSeoPresent && (
            <>
              <meta name="description" content={metaDescription} />

              {/* Open Graph */}
              <meta property="og:type" content="website" />
              <meta property="og:title" content={title} />
              <meta property="og:description" content={metaDescription} />
              <meta property="og:image" content={ogImage} />
              <meta property="og:image:width" content={String(ogWidth)} />
              <meta property="og:image:height" content={String(ogHeight)} />
              <meta property="og:url" content={ogUrl} />
              <meta property="og:url" content={ogUrl} />   
              {/* Twitter */}
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={title} />
              <meta name="twitter:description" content={metaDescription} />
              <meta name="twitter:image" content={ogImage} />

              {/* Canonical vain, jos EI esikatselu */}
              {!isPreview && (
                <link rel="canonical" href={canonicalUrl.endsWith("/") ? canonicalUrl : `${canonicalUrl}/`} />
              )}
            </>
          )}

          {/* Palvelimella on jo metat → yliaja vain vaihtuva kuva + og:url */}
          {serverSeoPresent && (
            <>
              <meta property="og:image" content={ogImage} />
              <meta name="twitter:image" content={ogImage} />
              <meta property="og:url" content={canonicalUrl} />
              {/* HUOM: ei canonicalia enää, koska se on jo serveriltä */}
            </>
          )}
        </Helmet>


        <div className="h-10"></div>

        {/* Bändin kansikuva ja nimi */}
        <div data-fade className="max-w-6xl mx-auto mt-6">
         
         {!(isPreview || showInactiveOverlay) && band && (
            <div className="mx-5 mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Breadcrumbs
                items={[
                  { label: "Etusivu", to: "/" },
                  { label: "Esiintyjä" },
                  { label: band.name },
                ]}
              />

              {/* Nappirivi: mobiilissa toisinpäin → flex-row-reverse */}
              <div className="flex items-center gap-2 justify-center flex-row-reverse sm:flex-row">
                {/* Kopioi osoite – NeonButton */}
                <ResponsiveButton
                  type="button"
                  variant="secondary"
                  onClick={handleCopyBandUrl}
                  className="scale-[0.60] md:scale-[0.80]"
                >
                  Kopioi osoite
                </ResponsiveButton>

                {/* Takaisin-nappi niin kuin ennenkin */}
                <ResponsiveButton
                  className="scale-[0.80] md:scale-100"
                  onClick={() => {
                    if (window.history.length > 2) navigate(-1);
                    else navigate("/");
                    setTimeout(
                      () => window.scrollTo({ top: 0, behavior: "smooth" }),
                      100
                    );
                  }}
                  variant="primary"
                >
                  <div className="flex items-center gap-2">
                    <MdArrowBack className="text-xl" />
                    <span>Takaisin</span>
                  </div>
                </ResponsiveButton>
              </div>
            </div>
          )}

          <h1 className="font-customFont2 dark:text-shadow-lg text-center text-black dark:text-primary text-3xl sm:text-4xl md:text-5xl mdd:text-5xl lg:text-5xl xl:text-6xl 2xl:text-6xl font-bold leading-tight mb-4">
            {band.name}
          </h1>

          <div className="max-w-[1104px] mx-auto overflow-visible rounded-lg shadow-md">
            <img
              src={coverSrc}
              alt={band.name}
              title={`${band.name} – kansikuva`}
              loading="eager"
              decoding="async"
              width={band.cover_w || 1200}   // jos sinulla on mitat, käytä niitä
              height={band.cover_h || 800}   // muuten kohtuullinen oletus
              className="w-full h-auto object-cover"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = BAND_PLACEHOLDER; }}
            />
          </div>
        </div>

        {/* Kuvaus */}
        {band.description && (
          <div
            data-fade
            className="max-w-6xl mx-auto mt-4 p-4 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-md"
          >
            <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-semibold">
              {band.description.split("\n").map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </p>
          </div>
        )}

        {/* Galleria */}
        {(() => {
          // 1) valitse lähde: uusi -> legacy
          const rawList = (Array.isArray(band?.gallery) && band.gallery.length)
            ? band.gallery.map(g => g?.url || g?.image_url || g?.src || g)
            : safeParseArray(band?.gallery_images);

          // 2) muodosta kuvalista ja deduplikoi varmuuden vuoksi
          const seen = new Set();
          const images = rawList
            .map((it, i) => {
              const raw = typeof it === "string" ? it : it?.url || it?.image_url || it?.src || "";
              const src = absolutize(raw);
              if (!src) return null;
              const key = src.replace(/^https?:\/\/[^/]+/i, ""); // poista origin deduplikointia varten
              if (seen.has(key)) return null;
              seen.add(key);
              return {
                url: src, // BandGallery lukee url-avaimen
                alt: `Bändi ${band.name} galleria kuva #${i + 1}`,
                title: `${band.name} – galleria #${i + 1}`,
              };
            })
            .filter(Boolean);

          return images.length ? (
            <div
              data-fade
              className="max-w-6xl mx-auto p-4 mt-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-md"
            >
              <BandGallery images={images} bandName={band.name} />
            </div>
          ) : null;
        })()}

        {/* Videot + Tiedot (palautetaan alkuperäinen asettelu) */}
        <div
          data-fade
          className="mt-6 max-w-6xl mx-auto p-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-lg grid grid-cols-1 md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6"
        >
          {/* Videot (oma kortti + otsikko) */}
          {videoItems.length > 0 && (
            <div className="flex flex-col">
              <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl sm:text-3xl md:text-3xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold leading-tight mb-4">
                Videot
              </h3>
              <div className="space-y-6">
                {videoItems.map(({ embed }, idx) => (
                  <div key={idx} className="w-full overflow-hidden rounded-lg">
                  <iframe
                    width="100%"
                    height="315"
                    src={embed}
                    title={`Video ${idx + 1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    className="rounded-lg w-full h-[315px]"
                    loading="lazy"          // <-- lisätty
                  />

                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tiedot */}
          <div className="flex flex-col">
            <div className="ml-4">
              <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl sm:text-3xl md:text-3xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold leading-tight mb-4">
                Tiedot
              </h3>
              {genreText && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Musiikkityylit:</span>{" "}
                  {genreText}
                </p>
              )}

              {regionsText && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Esiintymisalueet:</span>{" "}
                  {regionsText || "Ei määritelty"}
                </p>
              )}

              {band.artist_type && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Esiintyjätyyppi:</span>{" "}
                  {band.artist_type}
                </p>
              )}
              {setsText && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Tarjottavat setit:</span> {setsText}
                </p>
              )}
 
              {instrumentsText && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Soittimet:</span> {instrumentsText}
                </p>
              )}

              {servicesText && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Muut palvelut:</span> {servicesText}
                </p>
              )}

              {band.feedback && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Palaute:</span>{" "}
                  {band.feedback}
                </p>
              )}
              {band.location && (
                <p className="font-customFont1 text-left text-black/80 dark:text-primary text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-normal">
                  <span className="font-extrabold">Paikkakunnalta:</span>{" "}
                  {band.location}
                </p>
              )}

              {/* Arvostelut */}
              {ratingCount > 0 ? (
                <div
                  className="flex items-center my-4"
                  aria-label={`Arvosana ${ratingAvg.toFixed(1)} / 5, ${ratingCount} arvostelua`}
                  role="img"
                >
                  <span className="font-customFont1 text-lg text-black/80 dark:text-primary">
                    {ratingAvg.toFixed(1)} / 5
                  </span>

                  <div className="ml-2 flex">
                    {Array.from({ length: Math.floor(ratingAvg) }).map((_, i) => (
                      <FaStar
                        key={`f${i}`}
                        className="text-yellow-400 drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]"
                      />
                    ))}
                    {ratingAvg - Math.floor(ratingAvg) >= 0.5 ? (
                      <span
                        key="half"
                        className="relative inline-block"
                        style={{ width: "1em", height: "1em" }}
                      >
                        <FaRegStar className="text-gray-400 absolute inset-0" />
                        <FaStar
                          className="text-yellow-400 absolute inset-0"
                          style={{ clipPath: "inset(0 50% 0 0)" }}
                        />
                      </span>
                    ) : null}
                    {Array.from({
                      length:
                        5 -
                        Math.floor(ratingAvg) -
                        (ratingAvg - Math.floor(ratingAvg) >= 0.5 ? 1 : 0),
                    }).map((_, i) => (
                      <FaRegStar key={`e${i}`} className="text-gray-400" />
                    ))}
                  </div>

                  <span className="font-customFont1 text-lg font-bold ml-3 text-black/80 dark:text-primary">
                    ({ratingCount})
                  </span>
                </div>
              ) : (
                <p className="font-customFont1 text-lg font-bold my-4 text-black/80 dark:text-primary">
                  Ei vielä arvosteluja
                </p>
              )}

              {/* Somekanavat */}
              {(facebook || instagram || tiktok || youtube) && (

                <div
                  data-fade
                  className="max-w-6xl mx-auto flex space-x-6 justify-center mt-6"
                >
                  {normalize(facebook) && (
                    <a
                      href={normalize(facebook)}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      referrerPolicy="no-referrer"
                      aria-label={`${band.name} Facebook`}
                      className="group relative transition-transform transform hover:scale-125 duration-300 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                    >
                      <FaFacebook
                        size={35}
                        className="text-blue-600 hover:text-blue-700 transition-colors duration-300"
                      />
                      <span className="absolute z-50 -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 bg-blue-600 text-white text-xs px-2 py-1 rounded-md">
                        Facebook
                      </span>
                    </a>
                  )}

                  {normalize(instagram) && (
                    <a
                      href={normalize(instagram)}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      referrerPolicy="no-referrer"
                      aria-label={`${band.name} Instagram`}
                      className="group relative transition-transform transform hover:scale-125 duration-300 focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:outline-none"
                    >
                      <FaInstagram size={35} style={{ color: "#E1306C" }} />
                      <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 bg-pink-500 text-white text-xs px-2 py-1 rounded-md">
                        Instagram
                      </span>
                    </a>
                  )}

                  {normalize(tiktok) && (
                    <a
                      href={normalize(tiktok)}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      referrerPolicy="no-referrer"
                      aria-label={`${band.name} TikTok`}
                      className="group relative transition-transform transform hover:scale-125 duration-300 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:outline-none"
                    >
                      <FaTiktok className="text-black dark:text-white" size={35} />
                      <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 bg-gray-800 text-white text-xs px-2 py-1 rounded-md">
                        TikTok
                      </span>
                    </a>
                  )}

                  {normalize(youtube) && (
                    <a
                      href={normalize(youtube)}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      referrerPolicy="no-referrer"
                      aria-label={`${band.name} YouTube`}
                      className="group relative transition-transform transform hover:scale-125 duration-300 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none"
                    >
                      <FaYoutube className="text-red-600" size={35} />
                      <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-y-2 transition-all duration-300 bg-red-600 text-white text-xs px-2 py-1 rounded-md">
                        YouTube
                      </span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* SoundCloud -embeddit */}
        {(() => {
          // Hyväksyy stringit ja objektit ({ url, link, href })
          const extractSoundCloudUrls = (listLike) =>
            (safeParseArray(listLike) || [])
              .map((item) => {
                if (!item) return "";
                if (typeof item === "string") return item.trim();
                if (typeof item === "object") {
                  const u = item.url || item.link || item.href || "";
                  return String(u || "").trim();
                }
                return String(item).trim();
              })
              .filter((u) => u && /soundcloud\.com/i.test(u));

          const scUrls = extractSoundCloudUrls(band.audio_tracks);

          return scUrls.length > 0 ? (
            <div
              data-fade
              className="mt-6 max-w-6xl mx-auto p-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-lg space-y-6"
            >
              {/* Otsikko ennen soittimia */}
              <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl sm:text-3xl md:text-3xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold leading-tight mb-4">
                Audio
              </h3>

              <div className="space-y-6">
                {scUrls.map((trackUrl) => (
                  <div key={trackUrl} className="w-full overflow-hidden rounded-lg">
                    <iframe
                      width="100%"
                      height="200"
                      allow="autoplay"
                      scrolling="no"
                      frameBorder="no"
                      title={`soundcloud-track-${band?.slug || "band"}-${btoa(
                        trackUrl
                      ).slice(0, 8)}`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      className="w-full rounded-lg"
                      src={`https://w.soundcloud.com/player/?${new URLSearchParams({
                        url: trackUrl,
                        auto_play: "false",
                        hide_related: "false",
                        show_comments: "true",
                        show_user: "true",
                        show_reposts: "false",
                        visual: "true",
                      })}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}


      <div className="hidden md:block max-w-6xl mx-auto mt-6">
        <BookingHint />
      </div>

        {/* Kalenteri + lomake */}

        <div
          data-fade
          className="max-w-6xl mx-auto mt-6 grid grid-cols-1 md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6"
        >
              <div
                ref={(el) => { calRootRef.current = el; }}
            className="p-6 bg-lemon/50 dark:bg-night-700/50 rounded-lg"
          >
            <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl font-bold leading-tight">
              Keikkakalenteri
            </h3>
              <GigCalendar
                key={band.id}                 // remounttaa kun bändi vaihtuu
                bandId={band.id}
                onDateSelect={handleDateSelect}
                refreshKey={refreshKey}
                initialDate={initialDate}
                enableMobileScroll={true}
                scrollTargetSelector="#contact-form, [data-contact-form]"
                onHasMarkedChange={handleHasMarkedChange}
              />

          </div>

          <div
            ref={formRootRef}
            className="relative p-6 bg-lemon/60 dark:bg-night-700/50 rounded-lg shadow-lg"
          >
            <h3 className="font-customFont2 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-3xl font-bold leading-tight mb-4">
              Yhteydenottolomake
            </h3>

            {/* Mobiili: näytä varausohje heti lomakkeen yläpuolella */}
            <div className="block md:hidden mb-3">
              <BookingHint />
            </div>

         {/* VARATTU – näytetään vain tarjouspyyntötilassa, ei viesti/palaute-linkistä */}

          {selectedDate?.status === "booked" &&
            !forceFeedback &&                  // ei koskaan palautelinkin tapauksessa
            formMode !== "message" &&          // ei viestitilassa
            formMode !== "feedback" && (       // ei palautetilassa
              <div
                role="alert"
                aria-live="assertive"
                className="
                  my-2 mx-4 text-center font-customFont2 font-medium
                  text-black/80 dark:text-primary
                  bg-emerald-50 dark:bg-emerald-900/30
                  dark:border-emerald-800
                  rounded-xl px-2 py-3
                  text-xs md:text-sm
                "
              >
                <span aria-hidden="true" className="mr-1 text-xs">⛔</span>
                Valitettavasti {(band?.name || band?.band_name || "Esiintyjä")} ei ole vapaana
                {selectedDate?.formatted ? (
                  <>
                    {" "}päivälle{" "}
                    <strong className="font-semibold">
                      {selectedDate.formatted}
                    </strong>.
                  </>
                ) : (
                  <> valitsemallesi päivälle.</>
                )}

                <span className="block text-xs mt-1 opacity-90">
                  Valitse kalenterista toinen päivä tai jätä viesti esiintyjälle.{" "}
                  Vapaana olevat päivät on merkitty{" "}
                  <span className="font-semibold">vihreällä</span>.
                </span>
              </div>
          )}


            {/* VAPAANA – näytetään vain tarjouspyynnölle */}
            {selectedDate?.formatted &&
              isOpen &&
              !isPastSelected &&
              !quoteSent &&
              !isFeedbackActive &&             // ei palaute moodissa
              (formMode === "quote" || formMode === null) && (  // vain “Tarjouspyyntö”-tilassa
              <p
                role="status"
                aria-live="polite"
                className="
                  my-2 mx-4 text-center font-customFont2 font-medium
                  text-black/80 dark:text-primary
                  bg-emerald-50 dark:bg-emerald-900/30
                  dark:border-emerald-800
                  rounded-xl px-2 py-3
                  text-xs md:text-sm
                "
              >
                <span className="inline-block mr-1" aria-hidden="true">✓</span>
                Esiintyjä on vapaa päivänä<br />
                <strong className="font-extrabold underline">{selectedDate.formatted}</strong>
                <br />
                Voit siis kysyä tarjousta!
              </p>
            )}


            {/* MENNEISYYDESSÄ (punainen varoitus, jos status=open mutta päivä mennyt) */}
            {selectedDate?.formatted && isOpen && isPastSelected && !isFeedbackActive && (

              <div
                role="alert"
                aria-live="assertive"
                className="
                  my-2 mx-4 text-center font-customFont2 font-medium
                  text-black/80 dark:text-primary
                  bg-emerald-50 dark:bg-emerald-900/30
                  dark:border-emerald-800
                  rounded-xl px-2 py-3
                  text-xs md:text-sm
                "
              >
                <span className="inline-block mr-1" aria-hidden="true">⛔</span>
                Päivä on menneisyydessä <strong className="font-extrabold">{selectedDate.formatted}</strong> –
                tarjousta ei voi pyytää.
              </div>
            )}

            {/* SULJETTU */}
            {selectedDate?.status === "closed" && (
              <div className=" my-2 mx-4 text-center font-customFont2 font-medium
                  text-black/80 dark:text-white/80
                  bg-emerald-50 dark:bg-emerald-900/30
                  dark:border-emerald-800
                  rounded-xl px-2 py-3
                  text-xs md:text-sm">
                <p className="font-medium">Tämä päivä ei ole buukattavissa</p>
                <p className="mt-1 text-sm opacity-90">
                  Valitse kalenterista <span className="inline-flex items-center gap-1 align-middle">
                    <span className="h-3 w-3 rounded bg-emerald-500" aria-hidden />
                    <span className="font-medium">vihreä</span>
                  </span>{" "}
                  päivä.
                </p>
              </div>
            )}

            {/* PENDING */}
          {selectedDate?.status === "pending" && (
            <div className="rounded-lg p-4 text-center my-4 ring-1 bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:ring-amber-700/50">
              <p className="mb-1 font-semibold">
                Tälle keikkapäivälle on tarjouspyyntö vireillä.
              </p>
              <p className="text-sm opacity-90">
                Päivä on nyt pidossa tarjouspyynnön tekijälle. Pito raukeaa{" "}
                <strong>{timeLeft ?? "..."}</strong>, jos varausta ei vahvisteta ja
                varausmaksua makseta siihen mennessä.
              </p>
            </div>
          )}

            {/* BLACKOUT */}
            {selectedDate?.status === "blackout" && (
              <div
                role="alert"
                className="rounded-lg p-4 text-center my-4 ring-1 bg-gray-50 text-gray-900 ring-gray-200 dark:bg-gray-800/60 dark:text-gray-100 dark:ring-gray-600/60"
              >
                Esiintyjä ei ole buukattavissa valittuna päivänä. Valitse toinen päivä kalenterista.
              </div>
            )}

            <div className={isPreview ? "pointer-events-none opacity-60" : ""}>
              <BookingForm
                key={selectedDate?.iso || "no-date"}
                bandSlug={band.slug}
                selectedDate={selectedDate ?? null}
                defaultMode={forceFeedback ? "feedback" : (isOpen ? "quote" : "message")}
                quoteDisabled={!isOpen || isPastSelected}
                hideQuoteOption={!isOpen || isPastSelected}
                hideInlineBanner={true}   // 👈 sammuta lomakkeen oma statusbanneri
                hideSuccessCard={true}
                onModeChange={(m) => setFormMode(m)}
                onSubmitSuccess={({ mode, holdId, holdExpiresIso, status }) => {
                  setQuoteSent(true); // piilota vihreä banneri
                  if (mode === "quote") {
                    // näytä tämän komponentin "pending"-ilmoitus saman tien
                    setSelectedDate(prev => prev ? {
                      ...prev,
                      status: status || "pending",
                      expires_at: holdExpiresIso || prev.expires_at
                    } : prev);
                  }
                }}
                prefill={{
                  email: urlEmail,
                  date:  urlDate,
                  pin:   urlPin,
                  feedback: forceFeedback,
                }}
                previewStatic={isPreview}
              />
            </div>
            {isPreview && (
              <div
                className="
                  absolute inset-0 rounded-lg grid place-items-center
                  bg-black/20 dark:bg-black/40
                "
                aria-hidden="true"
              >
                <div className="text-center dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm px-4 py-2 rounded-lg bg-white/90 dark:bg-black/70 ring-1 ring-black/10 dark:ring-white/10">
                  Esikatselussa lomake ei ole käytössä.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    {/* Kelluva "Varaa heti" -nappi:
    - mobiili: vie kalenterin kohdalle
    - desktop: vie lomakkeelle
        - keskitetty alas + bilemäinen syke
    */}

      {!isPreview && !showInactiveOverlay && showFloatingCta && (
        <div
          className="
            fixed
            left-1/2 -translate-x-1/2
            bottom-8 sm:bottom-10 md:bottom-12
            z-50
            pointer-events-none
          "
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <ResponsiveButton
            onClick={handleScrollToBooking}
            className="
              pointer-events-auto relative rounded-full px-7 py-3
              text-white font-semibold tracking-wide
              bg-gradient-to-r from-fuchsia-500 via-indigo-500 to-sky-400
              shadow-xl shadow-fuchsia-500/40
              dark:shadow-[0_0_36px_rgba(56,189,248,0.80)]
              transition-all duration-300
              hover:scale-[1.06]
              hover:shadow-[0_0_48px_rgba(244,63,94,0.95)]
              active:scale-95
              before:absolute before:-inset-1 before:rounded-full
              before:bg-fuchsia-500/30 dark:before:bg-sky-400/40
              before:blur before:-z-10
              before:animate-ping
            "
          >
            Varaa heti
          </ResponsiveButton>
        </div>
      )}

      {/* JULKAISTU, MUTTA KALENTERI EI TÄYTÄ EHTOJA → TUMMA VERHO */}
      {showInactiveOverlay ? inactiveOverlayEl : null}

      {/* ESIAKATSELUN KOKO SIVUN OVERLAY + CTA */}

      {isPreview && fromDashboard && !showInactiveOverlay && (
        <div className="fixed inset-0 z-[70] grid place-items-center pointer-events-none p-4">
          {/* tumma overlay ilman bluria */}
          <div className="absolute inset-0 bg-black/45" aria-hidden="true" />

        </div>
      )}

    </PageFade>

    {/* Paikallinen ToastContainer jos globaalia ei ole */}
  <ToastContainer position="top-right" autoClose={4000} theme="colored" />
  </>
  );
};

export default BandProfile;
